import { Box, ButtonBase, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { layoutTokens, typographyTokens } from "@/shared/theme";
import type { SummaryTile } from "../pattern-summary";

const tileSx = {
  display: "block",
  textAlign: "left",
  p: 1.5,
  minWidth: 0,
  border: layoutTokens.dividerBorder,
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;
const actionSx = { ...tileSx, "&:hover": { bgcolor: "action.hover" } } as const;

const TileBody = ({ tile, action }: { tile: SummaryTile; action: string | null }) => (
  <>
    <Typography
      sx={{
        fontSize: "caption.fontSize",
        fontWeight: "fontWeightBold",
        letterSpacing: typographyTokens.tracking.label,
        color: "text.secondary",
      }}
    >
      {tile.label}
    </Typography>
    <Typography sx={{ fontSize: "h6.fontSize", fontWeight: "fontWeightBold", my: 0.25 }}>{tile.value}</Typography>
    <Typography sx={{ fontSize: "caption.fontSize", color: "text.secondary" }}>{tile.note}</Typography>
    {action && (
      <Typography sx={{ mt: 0.5, fontSize: "caption.fontSize", fontWeight: "fontWeightBold", color: "primary.main" }}>
        {action}
      </Typography>
    )}
  </>
);

/** Compact measured habits; a tile opens its board in the explorer when a position exists. */
export const SummaryStrip = ({ tiles, onOpenItem }: { tiles: SummaryTile[]; onOpenItem: (id: string) => void }) => (
  <Box component="section" aria-label="Summary" data-tour="summary-strip" sx={{ mt: 3 }}>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 1 }}>
      {tiles.map((tile) => {
        const itemId = tile.itemId;
        if (itemId)
          return (
            <ButtonBase key={tile.id} data-summary-item={itemId} onClick={() => onOpenItem(itemId)} sx={actionSx}>
              <TileBody tile={tile} action="SHOW BOARD →" />
            </ButtonBase>
          );
        if (tile.to)
          return (
            <ButtonBase key={tile.id} component={RouterLink} to={tile.to} sx={actionSx}>
              <TileBody tile={tile} action={tile.to.startsWith("/games/") ? "REVIEW GAME →" : "SEE TRENDS →"} />
            </ButtonBase>
          );
        return (
          <Box key={tile.id} sx={tileSx}>
            <TileBody tile={tile} action={null} />
          </Box>
        );
      })}
    </Box>
    <Link
      component={RouterLink}
      to="/trends"
      sx={{ display: "inline-block", mt: 1, fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}
    >
      See Trends →
    </Link>
  </Box>
);
